import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Technology from "@/pages/Technology";
import Products from "@/pages/Products";
import Patents from "@/pages/Patents";
import ClinicalRecovery from "@/pages/ClinicalRecovery";
import Platform from "@/pages/Platform";
import Intelligence from "@/pages/Intelligence";
import Applications from "@/pages/Applications";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Vision from "@/pages/Vision";
import GlobalHealth from "@/pages/GlobalHealth";
import WhyWearSpray from "@/pages/WhyWearSpray";
import Science from "@/pages/Science";
import Market from "@/pages/Market";
import PitchDeck from "@/pages/PitchDeck";
import ValveDemo from "@/pages/ValveDemo";
import Resume from "@/pages/Resume";
import IQLanding from "@/pages/IQLanding";
import IQAdmin from "@/pages/IQAdmin";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import NewsletterHub from "@/pages/NewsletterHub";
import NewsletterArticle from "@/pages/NewsletterArticle";
import Analytics from "@/components/Analytics";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Route <title> and meta tags are owned by the <SEO> component
    // (react-helmet-async) on each page — do not set document.title here.
    // Run after the new route has painted so Lenis measures correctly.
    const id = requestAnimationFrame(() => {
      const lenis = window.lenis;
      if (hash) {
        const el = document.querySelector(hash);
        if (el) {
          if (lenis) lenis.scrollTo(el, { immediate: true });
          else el.scrollIntoView();
          return;
        }
      }
      // No hash (or target missing) → force the very top.
      if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
      window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.lenis = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      delete window.lenis;
    };
  }, []);
  return null;
}

// Headless crawlers (react-snap's static pre-render, Puppeteer-based bots)
// set navigator.webdriver = true. Autoplaying/looping <video> elements keep
// the network from ever going idle, which stalls the crawler's page-load
// wait indefinitely. Pausing every video for those visitors keeps every
// route's static HTML generation fast and reliable; real visitors are
// unaffected.
function CrawlerVideoGuard() {
  useEffect(() => {
    if (!navigator.webdriver) return;
    const pauseAll = () => {
      document.querySelectorAll("video").forEach((v) => {
        v.loop = false;
        v.autoplay = false;
        v.pause();
      });
    };
    pauseAll();
    const observer = new MutationObserver(pauseAll);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
}

// The inner app tree, without a router — App.js (below) wraps this with
// BrowserRouter for real browsers; the Node-based prerender script
// (scripts/prerender.js) wraps it with StaticRouter instead, one route at a
// time, so no browser/Puppeteer/Chromium is needed at build time.
export function AppShell() {
  const { pathname } = useLocation();
  const isImmersive = pathname === "/investors" || pathname === "/technology/valve" || pathname === "/resume" || pathname === "/iq" || pathname === "/iq/admin";
  return (
    <div className="App grain bg-[#0a0a0a]">
      <SmoothScroll />
      <ScrollToTop />
      <CrawlerVideoGuard />
      <Analytics />
      {!isImmersive && <Navbar />}
      <main className="relative z-[2]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/why" element={<WhyWearSpray />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/science" element={<Science />} />
          <Route path="/products" element={<Products />} />
          <Route path="/patents" element={<Patents />} />
          <Route path="/clinical" element={<ClinicalRecovery />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/intelligence" element={<Intelligence />} />
          <Route path="/applications" element={<Applications />} />
          <Route path="/vision" element={<Vision />} />
          <Route path="/global-health" element={<GlobalHealth />} />
          <Route path="/market" element={<Market />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/investors" element={<PitchDeck />} />
          <Route path="/technology/valve" element={<ValveDemo />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/iq" element={<IQLanding />} />
          <Route path="/iq/admin" element={<IQAdmin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/newsletter" element={<NewsletterHub />} />
          <Route path="/newsletter/:slug" element={<NewsletterArticle />} />

          {/* 301-equivalent redirects from the old Shopify-era URLs (SEO work order 3.2.6) */}
          <Route path="/pitch-deck" element={<Navigate to="/investors" replace />} />
          <Route path="/pages/about" element={<Navigate to="/about" replace />} />
          <Route path="/pages/repellents" element={<Navigate to="/products" replace />} />
          <Route path="/pages/malaria-bug-band-pro" element={<Navigate to="/global-health" replace />} />
          <Route path="/products/clean-band" element={<Navigate to="/products" replace />} />
          <Route path="/products/clear-semi-transparent" element={<Navigate to="/products" replace />} />
          <Route path="/pages/opportunity" element={<Navigate to="/market" replace />} />
          <Route path="/pages/terms-conditions" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isImmersive && <Footer />}
      <Toaster position="bottom-center" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
